import { useEffect } from 'react';

import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppState } from '../../../AppStateProvider';
import type { Contact } from '../../../domain/contact';
import { useHasTimedOut } from '../../../hooks/useHasTimedOut';
import { appStore } from '../../../services/appStore';
import { contactsApi } from '../api';
import { createLinkedinContactsPayload } from '../utils/createLinkedinContactsPayload';

import linkedinContactQueryKey from './linkedinContactQueryKey';

export const useLinkedinContactQuery = (): UseQueryResult<Contact | null> => {
  const appState = useAppState();
  const queryClient = useQueryClient();
  const { isOpen, contactLinkedinId, contactLinkedinSalesNavId, collectedContactInfo } = appState;
  const companyIds = collectedContactInfo?.numericCompanyLinkedinIds || [];
  const queryKey = linkedinContactQueryKey.fromState(appState);

  const hasTimedOut = useHasTimedOut();

  const isEnabled = () => {
    if (!isOpen) {
      return false;
    }

    if (hasTimedOut && (contactLinkedinId || contactLinkedinSalesNavId)) {
      return true;
    }

    return hasTimedOut && !!companyIds.length && !!collectedContactInfo?.name;
  };

  const fetchLinkedinContact = async ({ signal }: { signal?: AbortSignal }) => {
    const collectedContact = {
      ...collectedContactInfo,
      linkedinId: contactLinkedinId,
      linkedinSalesNavId: contactLinkedinSalesNavId,
    };
    const payload = createLinkedinContactsPayload([collectedContact]);

    if (payload.length === 0) {
      return null;
    }

    const contacts = await contactsApi.getLinkedinContacts(payload, signal);

    if (contacts?.[0]) {
      queryClient.setQueryData(linkedinContactQueryKey.fromData(contacts?.[0]), query.data);
    }

    return contacts?.[0] || null;
  };

  const query = useQuery({
    queryKey,
    queryFn: fetchLinkedinContact,
    enabled: isEnabled(),
    retry: false,
    refetchInterval: false,
    placeholderData: (previousData, previousQuery) => {
      if (previousQuery && queryKey[1] === previousQuery.queryKey[1]) {
        return previousData;
      }
      return undefined;
    },
  });

  useEffect(() => {
    if (query.status !== 'success' || !query.data) {
      return;
    }
    appStore.setCompanyDomain(query.data?.originalSite);
  }, [query.status, contactLinkedinId]);

  return query;
};
